import React, { useState, useEffect } from "react";

import MainInput from "../../Elements/MainInput/MainInput";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import MainButton from "../../Elements/MainButton/MainButton";
import RefundModal from "../RefundModal/RefundModal";
import Switch from "../../Elements/Switch/Switch";

import { fetchDisableInstructions, updateAutoTopup } from "../../../services/EsimService";
import copyIcon from "../../../assets/copy-alt.png";
import "./EsimActions.css";

const EsimActions = ({ selectedEsim, deviceType, onToggleTopUp, isTopUpOpen }) => {
  const [copiedField, setCopiedField] = useState(null);

  const [disableSteps, setDisableSteps] = useState([]);
  const [loadingDisable, setLoadingDisable] = useState(false);

  const [isAutoRenew, setIsAutoRenew] = useState(selectedEsim?.automatic_topup || false);
  const [isUpdatingAutoRenew, setIsUpdatingAutoRenew] = useState(false);

  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);

  const isTopUpAvailable =
    selectedEsim?.top_up_type !== null &&
    selectedEsim?.top_up_type !== undefined &&
    selectedEsim?.status !== "terminated";

  useEffect(() => {
    if (selectedEsim) {
      setIsAutoRenew(selectedEsim.automatic_topup || false);
    }
  }, [selectedEsim]);

  useEffect(() => {
    if (deviceType) {
      setLoadingDisable(true);

      fetchDisableInstructions(deviceType)
        .then((data) => {
          const prefix = deviceType.toUpperCase() === "IOS" ? "IOS" : deviceType.toLowerCase();
          const key = `${prefix}DisableSteps`;

          setDisableSteps(data?.[key] || []);
        })
        .catch((err) => console.error(err))
        .finally(() => setLoadingDisable(false));
    }
  }, [deviceType]);

  const handleAutoRenewChange = async (newValue) => {
    if (!selectedEsim?.iccid) return;

    const previousValue = isAutoRenew;
    setIsAutoRenew(newValue);
    setIsUpdatingAutoRenew(true);

    try {
      await updateAutoTopup(selectedEsim.iccid, newValue);
      console.log("Auto-renew updated successfully to:", newValue);
    } catch (error) {
      console.error("Failed to update auto-renew, rolling back.");
      setIsAutoRenew(previousValue);
    } finally {
      setIsUpdatingAutoRenew(false);
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const renderStep = (step) => {
    return step.replace(/\*(.*?)\*/g, '<span style="font-weight:600">$1</span>');
  };

  const displayIccid = selectedEsim?.iccid ? selectedEsim.iccid.slice(-10) : "";

  return (
    <div className="esim-actions">
      <div className="esim-field">
        <div className="esim-id-section">
          <label>eSIM ID</label>
          <div className="input-with-copy">
            <MainInput value={displayIccid} readOnly placeholder="0000000000" />
            <button
              className={`copy-btn ${copiedField === "iccid" ? "copied" : ""}`}
              onClick={() => copyToClipboard(displayIccid, "iccid")}
              title="Copy to clipboard"
            >
              <img src={copyIcon} alt="Copy" />
            </button>
          </div>
        </div>

        {isTopUpAvailable && (
          <div className="auto-renew-section">
            <div className="auto-renew-text">
              <label>Auto-renewal</label>
              <p>The plan will automatically renew</p>
            </div>

            <Switch
              checked={isAutoRenew}
              onChange={handleAutoRenewChange}
              disabled={isUpdatingAutoRenew || !selectedEsim}
            />
          </div>
        )}

        {isTopUpAvailable && (
          <div className="actions-buttons-row">
            <SecondButton onClick={() => setIsRefundModalOpen(true)}>Refund</SecondButton>
            <MainButton onClick={onToggleTopUp}>{isTopUpOpen ? "Hide Top-Up" : "Top-Up"}</MainButton>
          </div>
        )}
        <div className="disable-section">
          <label>Disable eSIM</label>
          <div className="disable-instructions-container">
            {loadingDisable ? (
              <div className="loading-text">Loading instructions...</div>
            ) : disableSteps.length > 0 ? (
              <ol className="instruction-steps">
                {disableSteps.map((step, index) => (
                  <li key={`disable-${index}`} dangerouslySetInnerHTML={{ __html: renderStep(step) }} />
                ))}
              </ol>
            ) : (
              <div className="no-instructions">No instructions available</div>
            )}
          </div>
        </div>
        <div className="warning-card">
          <p>
            WARNING! Most eSIMs can only be installed once. If you remove the eSIM from your device, you cannot install
            it again.
          </p>
        </div>
        {!isTopUpAvailable && (
          <div className="actions-button-row">
            <SecondButton onClick={() => setIsRefundModalOpen(true)}>Refund</SecondButton>
          </div>
        )}
      </div>
      <RefundModal isOpen={isRefundModalOpen} onClose={() => setIsRefundModalOpen(false)} iccid={selectedEsim?.iccid} />
    </div>
  );
};

export default EsimActions;
